import { Box } from '@mui/material';
import { FunctionComponent, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DetectedDefinition } from '../../schemas/DetectedDefinition';
import { replacePercent20ToSpace } from '../../utils/StringRelated';
import { DynamicRequestBody } from '../../schemas/DynamicRequestBody';
import { processQueryParameters } from '../../utils/useSearchHandler';
import { useProductGroups } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ProductGroupsProvider';
import { ReturnParameterValuesLite } from '../../schemas/ReturnParameterValuesLite';
import { Industrial_Sensors_Products_ID } from '../layout/Header/Product';
import Image from 'next/image';

interface SingleDefinitionContainerProps {
    definition: DetectedDefinition;
    isRootPage: boolean;
    language: string;
    queryRawResults?: ReturnParameterValuesLite;
    searchInput?: string | undefined;
}

// Use nonRoot styles for all cases
const containerSx = {
    width: '273px',
    height: '310px',
    background: '#FFFFFF',
    border: '1px solid #D3D3D3',
    borderRadius: '3px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    position: 'relative',
    overflow: 'visible',
    paddingTop: '2rem',
    '&:hover': {
        background: '#F5C3B24D',
        boxShadow: '0px 2px 14px rgba(128, 128, 128, 0.3)',
    },
    cursor: 'pointer',
};

const createImageBoxSx = (imageUrl: string | null | undefined) => ({
    width: '245px',
    height: '140px',
    backgroundImage: `url(${imageUrl || 'https://search.kemet.com/component-images/ceramic_chip_standard.jpg'})`,
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    position: 'absolute',
    top: '-2rem',
    left: '50%',
    transform: 'translateX(-50%)',
    borderRadius: '3px',
    cursor: 'pointer',
    margin: 0,
    padding: 0,
    backgroundRepeat: 'no-repeat',
});

const mainInfoSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    width: '245px',
    marginTop: '7rem',
    cursor: 'pointer',
    paddingTop: '1rem',
};

const displayNameSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '23px',
    textAlign: 'center',
    color: '#191A1B',
    height: '27px',
    width: '100%',
};

const descriptionSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: '20px',
    textAlign: 'center',
    color: '#454545',
    width: '100%',
    height: '60px',
    paddingTop: '1rem',
};

const SingleDefinitionContainer: FunctionComponent<SingleDefinitionContainerProps> = ({
    definition,
    isRootPage,
    language,
    queryRawResults,
    searchInput,
}) => {
    const location = useLocation();

    const { productGroupByDmDefinitionId, productGroupById } = useProductGroups();

    // 先直接從 mapping 取得 productGroup
    let productGroup = productGroupByDmDefinitionId[definition.id.toString()];

    // console.log(definition.id, definition.displayName, productGroup);

    const industrialSensorsProducts = Object.values(productGroupById).find(
        (p) => p?.id === '14670'
    );

    // 如果直接找不到，再遍歷所有群組的 children
    if (!productGroup) {
        for (const key in productGroupByDmDefinitionId) {
            const group = productGroupByDmDefinitionId[key];
            if (group?.children && Array.isArray(group.children)) {
                const childFound = group.children.find(
                    (child) => child.dmDefinitionId === definition.id.toString()
                );
                if (childFound) {
                    productGroup = childFound;
                    break;
                }
            }
        }
    }

    // 先計算好 targetPath 與導向 state（避免在 onClick 內呼叫副作用）
    const { targetPath, state } = useMemo(() => {
        const currentPath = location.pathname.replace(/\/$/, '');
        if (isRootPage) {
            const pathSegments = currentPath.split('/');
            const lastSegment = pathSegments[pathSegments.length - 1];
            if (lastSegment !== definition.name) {
                return {
                    targetPath: {
                        pathname: `${currentPath}/${encodeURIComponent(replacePercent20ToSpace(definition.name))}`,
                        ...(searchInput
                            ? { search: `?search=${encodeURIComponent(searchInput)}` }
                            : {}),
                    },
                    state: { queryRawResults, searchInput },
                };
            }
        } else {
            const prefixIndex = currentPath.indexOf('products/');
            const substring =
                prefixIndex !== -1 ? currentPath.substring(prefixIndex + 'products/'.length) : '';

            // --------- 建立搜尋參數 ---------
            const params = new URLSearchParams();

            // 若不是 Browse All，先放 79
            if (definition.displayName !== 'Browse All') {
                params.set('79', String(definition.id));
            }

            // 若有 searchInput，再放 search=xxx
            if (searchInput) {
                params.set('search', searchInput);
            }

            // --------- 組成 pathname + search ---------
            const targetPathObj = {
                pathname: `/products/${substring}`, // e.g. /products/resistors
                search: params.toString() // 可能是 '' | '79=123' | '79=123&search=abc'
                    ? `?${params.toString()}`
                    : undefined,
            };

            /* --------- 建立 state 如原本邏輯 --------- */
            const parameterValues = processQueryParameters(queryRawResults?.queryParameterValues);
            const initialFilterState: DynamicRequestBody = {
                '1': definition.parentDefinitionId || 1,
                ...parameterValues,
            };
            if (definition.name !== 'Browse All') {
                initialFilterState['79'] = [definition.id];
            }

            // 回傳
            return {
                targetPath: targetPathObj,
                state: queryRawResults
                    ? {
                          queryRawResults,
                          initialFilterState,
                          searchInput,
                          leftOverParam: searchInput,
                      }
                    : { shouldClearAllProps: true },
            };
        }

        // 如無符合條件，fallback 直接返回目前路徑
        return { targetPath: currentPath, state: {} };
    }, [location.pathname, isRootPage, definition, queryRawResults, searchInput]);

    const isIndustrialSensor = definition.id === Industrial_Sensors_Products_ID;

    const maskUrl = '/externalLink.svg';

    return (
        <Box
            component={isIndustrialSensor ? 'div' : Link}
            to={isIndustrialSensor ? undefined : targetPath}
            state={isIndustrialSensor ? undefined : state}
            data-testid={`${definition.displayName}_container`}
            sx={containerSx}
            onClick={
                isIndustrialSensor
                    ? () => window.open('https://www.telemecaniquesensors.com', '_blank')
                    : undefined
            }
        >
            {definition.id !== -1 && (
                <Box
                    data-testid={`${definition.displayName}_pic_zone`}
                    sx={createImageBoxSx(definition.definitionImage?.url)}
                >
                    <Box
                        component="img"
                        src={
                            definition.definitionImage?.url ||
                            'https://search.kemet.com/component-images/ceramic_chip_standard.jpg'
                        }
                        alt={definition.displayName}
                        sx={{
                            width: '100%',
                            height: '100%',
                            opacity: 0, // invisible
                        }}
                    />
                </Box>
            )}

            <Box data-testid={`${definition.displayName}_main_info_zone`} sx={mainInfoSx}>
                <Box data-testid={`${definition.displayName}_display_name`} sx={displayNameSx}>
                    {definition.displayNames.find((dn) => dn.language === language)?.display ||
                        definition.displayName}
                    {isIndustrialSensor && (
                        <Image
                            src={maskUrl}
                            alt="external link"
                            width={16}
                            height={16}
                            style={{
                                marginLeft: '0.5rem',
                                verticalAlign: 'middle',
                            }}
                        />
                    )}
                </Box>
                {definition.id !== -1 && (
                    <Box data-testid={`${definition.displayName}_description`} sx={descriptionSx}>
                        {isIndustrialSensor
                            ? industrialSensorsProducts?.shortBlurb
                            : productGroup?.shortBlurb ||
                              definition.description ||
                              'Non-polar devices which offer unsurpassed volumetric efficiency'}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SingleDefinitionContainer;
